import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Shield,
  ScrollText,
  Ban,
  Eye,
  Save,
  Send,
  Plus,
  Trash2,
  GripVertical,
  Tag,
  Clock,
  User,
  Globe,
  GitBranch,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { usePolicies, type PolicyKey, type PolicyDoc } from "@/lib/policies-store";
import { useTranslation } from "react-i18next";
import { useDirection } from "@/hooks/use-direction";

export const Route = createFileRoute("/admin/policies")({
  component: Page,
});

const TABS: { key: PolicyKey; icon: typeof Shield; route: string }[] = [
  { key: "privacy", icon: Shield, route: "/privacy" },
  { key: "terms", icon: ScrollText, route: "/terms" },
  { key: "cancellation", icon: Ban, route: "/cancellation" },
];

function Page() {
  const { t } = useTranslation();
  const [active, setActive] = useState<PolicyKey>("privacy");
  const current = TABS.find((tab) => tab.key === active)!;

  return (
    <AdminShell
      title={t("pages.admin.policies.title")}
      subtitle={t("pages.admin.policies.subtitle")}
      actions={
        <Button variant="outline" asChild className="rounded-xl">
          <Link to={current.route}>
            <Eye className="h-4 w-4" /> {t("pages.admin.policies.previewLive")}
          </Link>
        </Button>
      }
    >
      <Tabs value={active} onValueChange={(v) => setActive(v as PolicyKey)}>
        <TabsList className="mb-6 h-auto flex-wrap gap-1 rounded-xl bg-muted p-1">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              className="gap-2 rounded-lg px-4 py-2"
            >
              <tab.icon className="h-4 w-4" />
              {t(`pages.admin.policies.tabs.${tab.key}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((tab) => (
          <TabsContent key={tab.key} value={tab.key} className="mt-0">
            <PolicyEditor policyKey={tab.key} icon={tab.icon} previewRoute={tab.route} />
          </TabsContent>
        ))}
      </Tabs>
    </AdminShell>
  );
}

function PolicyEditor({
  policyKey,
  icon: Icon,
  previewRoute,
}: {
  policyKey: PolicyKey;
  icon: typeof Shield;
  previewRoute: string;
}) {
  const { t } = useTranslation();
  const dir = useDirection();
  const [policies, setPolicies] = usePolicies();
  const doc = policies[policyKey];

  const update = (patch: Partial<PolicyDoc>) => {
    setPolicies((s) => ({ ...s, [policyKey]: { ...s[policyKey], ...patch } }));
  };

  const updateSection = (idx: number, patch: Partial<PolicyDoc["sections"][number]>) => {
    const next = doc.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    update({ sections: next });
  };

  const addSection = () => {
    const id = `section-${Date.now()}`;
    update({
      sections: [...doc.sections, { id, title: t("adminPolicies.newSection"), body: "" }],
    });
    toast.success(t("adminPolicies.sectionAdded"));
  };

  const removeSection = (idx: number) => {
    update({ sections: doc.sections.filter((_, i) => i !== idx) });
    toast.success(t("adminPolicies.sectionRemoved"));
  };

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= doc.sections.length) return;
    const next = [...doc.sections];
    [next[idx], next[j]] = [next[j], next[idx]];
    update({ sections: next });
  };

  const saveDraft = () => toast.success(t("adminPolicies.draftSaved"));
  const publish = () => {
    update({ updated: new Date().toLocaleDateString(dir === "rtl" ? "ar-SA" : "en-US", { year: "numeric", month: "short", day: "numeric" }) });
    toast.success(t("adminPolicies.published", { title: doc.title }));
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <Input
                value={doc.title}
                onChange={(e) => update({ title: e.target.value })}
                className="h-auto border-0 bg-transparent p-0 font-display text-xl font-bold shadow-none focus-visible:ring-0"
              />
              <p className="text-xs text-muted-foreground">
                {t("adminPolicies.appliesTo")}{" "}
                <input
                  value={doc.appliesTo}
                  onChange={(e) => update({ appliesTo: e.target.value })}
                  className="border-0 bg-transparent outline-none"
                />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl" onClick={saveDraft}>
              <Save className="h-4 w-4" /> {t("adminPolicies.saveDraft")}
            </Button>
            <Button className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90" onClick={publish}>
              <Send className="h-4 w-4" /> {t("adminPolicies.publish")}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 text-[#5958F3]">
            <Tag className="h-3.5 w-3.5" /> {t("adminPolicies.legalDocument")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> {t("adminPolicies.lastUpdated", { date: doc.updated })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" /> {doc.author}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" /> {t("adminPolicies.effective", { date: doc.effective })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5" /> {doc.version}
          </span>
        </div>
      </Card>

      {/* Meta editor */}
      <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
        <h3 className="mb-4 text-sm font-semibold">{t("adminPolicies.metadata")}</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("adminPolicies.effectiveDate")}</Label>
            <Input value={doc.effective} onChange={(e) => update({ effective: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("adminPolicies.version")}</Label>
            <Input value={doc.version} onChange={(e) => update({ version: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("adminPolicies.author")}</Label>
            <Input value={doc.author} onChange={(e) => update({ author: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("adminPolicies.lastUpdatedLabel")}</Label>
            <Input value={doc.updated} onChange={(e) => update({ updated: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
            <Label className="text-xs">{t("adminPolicies.description")}</Label>
            <Textarea
              rows={2}
              value={doc.description}
              onChange={(e) => update({ description: e.target.value })}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3 sm:col-span-2 lg:col-span-2">
            <div>
              <p className="text-sm font-medium">{t("adminPolicies.numbered")}</p>
              <p className="text-xs text-muted-foreground">{t("adminPolicies.numberedHint")}</p>
            </div>
            <Switch
              checked={!!doc.numbered}
              onCheckedChange={(v) => update({ numbered: v })}
            />
          </div>
        </div>
      </Card>

      {/* Sections editor */}
      <Card className="rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 p-5">
          <div>
            <h3 className="text-sm font-semibold">{t("adminPolicies.sectionsTitle")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("adminPolicies.sectionsCount", { count: doc.sections.length })} • {t("adminPolicies.supports")}
              <code className="mx-1 rounded bg-muted px-1">**bold**</code>{t("adminPolicies.and")}
              <code className="mx-1 rounded bg-muted px-1">- bullet</code>{t("adminPolicies.lines")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" asChild>
              <Link to={previewRoute}>
                <Eye className="h-4 w-4" /> {t("adminPolicies.livePreview")}
              </Link>
            </Button>
            <Button size="sm" className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90" onClick={addSection}>
              <Plus className="h-4 w-4" /> {t("adminPolicies.addSection")}
            </Button>
          </div>
        </div>

        <div className="space-y-4 p-5">
          {doc.sections.map((section, idx) => (
            <div key={section.id} className="rounded-xl border border-border/60 bg-background p-4">
              <div className="mb-3 flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground">#{idx + 1}</span>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(idx, { title: e.target.value })}
                  className="flex-1 font-medium"
                  placeholder={t("adminPolicies.sectionTitlePlaceholder")}
                />
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label={t("adminPolicies.moveUp")}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => move(idx, 1)}
                    disabled={idx === doc.sections.length - 1}
                    aria-label={t("adminPolicies.moveDown")}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removeSection(idx)}
                    aria-label={t("adminPolicies.deleteSection")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("adminPolicies.anchorId")}</Label>
                  <Input
                    value={section.id}
                    onChange={(e) =>
                      updateSection(idx, { id: e.target.value.replace(/\s+/g, "-").toLowerCase() })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("adminPolicies.body")}</Label>
                  <Textarea
                    rows={5}
                    value={section.body}
                    onChange={(e) => updateSection(idx, { body: e.target.value })}
                    placeholder={t("adminPolicies.bodyPlaceholder")}
                  />
                </div>
              </div>
            </div>
          ))}
          {doc.sections.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("adminPolicies.empty")}
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
